/**
 * Governed Enrichment Pipeline — pure diff engine for CRM enrichment writes.
 *
 * Kills the dead triple-stub: enrichment is no longer a simulated blob of
 * fabricated "signals" written straight into records. Instead every
 * enrichment run produces an {@link EnrichProposal} — a before/after field
 * diff — that flows through the SAME tiered-autonomy + approvals machinery
 * as every other agent write:
 *
 *   worker fields (real Claude via argoWorker, fed in by the CALLER)
 *        │  unavailable / empty
 *        ▼
 *   deterministicEnrich(record)      ← NO-hallucination fallback: only
 *        │                              derives from data already present
 *        ▼
 *   buildProposal ──▶ classifyRisk ──▶ autoApplyDecision (tier per class)
 *        │                                  │ auto (low-risk, confident)
 *        │                                  │ gate → approvals inbox diff
 *        ▼                                  ▼
 *   toActionDiff — reversible, provenance-noted, ledger-sealable
 *
 * Honesty rules, locked by enrichPipeline.test.ts:
 *   • deterministicEnrich NEVER fabricates firmographics (employee count,
 *     funding, industry…) — it only normalizes what the record already
 *     holds (domain from email/url, titlecased name, canonical title,
 *     seniority tag derived from the title).
 *   • On the deterministic path, any field whose `after` cannot be
 *     re-derived from the record is stripped before it reaches the diff.
 *   • Pure and IO-free: no store, no network, no firebase. The worker path
 *     is data passed in by the caller (see argoWorker.ts / EnrichPanel).
 */

// --- Types -------------------------------------------------------------------

/** The autonomy classes enrichment writes are tiered on. */
export type EnrichClass =
  | 'firmographic'
  | 'contact-role'
  | 'domain-normalize'
  | 'title-normalize';

/** One before/after field change, with its origin and confidence. */
export interface EnrichField {
  field: string;
  before: any;
  after: any;
  source: 'worker' | 'deterministic';
  confidence: number;
}

/** A full enrichment proposal for one record — the reviewable diff. */
export interface EnrichProposal {
  recordId: string;
  recordType: 'account' | 'person';
  fields: EnrichField[];
  risk: 'low' | 'high';
  generatedBy: 'worker' | 'deterministic';
}

/** Tiered-autonomy policy for enrichment classes. */
export interface EnrichPolicy {
  tier: Record<EnrichClass, 'auto' | 'gate'>;
  /** Fields below this confidence ALWAYS gate, even in an 'auto' class. */
  minConfidence: number;
}

/** The reversible, provenance-noted diff handed to approvals/ledger. */
export interface EnrichActionDiff {
  recordId: string;
  changes: EnrichField[];
  reversible: true;
  provenanceNote: string;
}

/**
 * Conservative default policy: normalizations (derivable, low-blast-radius)
 * are auto above 0.8 confidence; contact-role and firmographic writes gate
 * to a human until that class has EARNED auto (autonomyEarnUp.ts).
 */
export const DEFAULT_ENRICH_POLICY: EnrichPolicy = {
  tier: {
    'domain-normalize': 'auto',
    'title-normalize': 'auto',
    'contact-role': 'gate',
    firmographic: 'gate',
  },
  minConfidence: 0.8,
};

// --- Field → class mapping ------------------------------------------------------

/** High-risk classes: a wrong write here misleads humans or reroutes deals. */
const HIGH_RISK_CLASSES: ReadonlySet<EnrichClass> = new Set<EnrichClass>([
  'contact-role',
  'firmographic',
]);

/** Map a field name onto its autonomy class. Unknown fields are firmographic —
 *  the most conservative class (always gated by the default policy). */
export function classOfField(field: string): EnrichClass {
  switch (field) {
    case 'domain':
    case 'website':
      return 'domain-normalize';
    case 'name':
    case 'title':
    case 'seniority':
      return 'title-normalize';
    case 'role':
    case 'stakeholderRole':
    case 'stakeholderRoles':
      return 'contact-role';
    default:
      return 'firmographic';
  }
}

// --- Deterministic derivations ---------------------------------------------------

/** Lowercase bare domain from an email address, or null. Never guesses. */
function domainFromEmail(email: unknown): string | null {
  if (typeof email !== 'string') return null;
  const at = email.trim().lastIndexOf('@');
  if (at <= 0 || at === email.trim().length - 1) return null;
  const domain = email.trim().slice(at + 1).toLowerCase();
  return domain.includes('.') ? domain : null;
}

/** Normalize a URL/website string down to its bare domain, or null. */
function domainFromUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim().toLowerCase();
  if (!trimmed) return null;
  const noProto = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const host = noProto.split(/[/?#]/)[0].replace(/^www\./, '').replace(/:\d+$/, '');
  return host.includes('.') ? host : null;
}

/** The one derivable domain for a record: email wins, then website/url. */
function deriveDomain(record: Record<string, any>): string | null {
  return (
    domainFromEmail(record.email) ??
    domainFromEmail(record.pocEmail) ??
    domainFromUrl(record.website) ??
    domainFromUrl(record.url)
  );
}

/** Words kept lowercase inside a titlecased name (never at the start). */
const NAME_PARTICLES = new Set(['de', 'da', 'van', 'von', 'del', 'la', 'le', 'of', 'the']);

/** Titlecase a personal/company name; hyphenated parts are cased per side. */
export function titleCaseName(name: string): string {
  const caseWord = (w: string): string =>
    w
      .split('-')
      .map((part) =>
        part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part,
      )
      .join('-');
  return name
    .trim()
    .split(/\s+/)
    .map((w, i) =>
      i > 0 && NAME_PARTICLES.has(w.toLowerCase()) ? w.toLowerCase() : caseWord(w),
    )
    .join(' ');
}

/** Canonical job-title map — source-of-truth normalization, never invention. */
const TITLE_CANON: Record<string, string> = {
  ceo: 'CEO',
  'chief executive officer': 'CEO',
  cto: 'CTO',
  'chief technology officer': 'CTO',
  cfo: 'CFO',
  'chief financial officer': 'CFO',
  coo: 'COO',
  'chief operating officer': 'COO',
  cio: 'CIO',
  cmo: 'CMO',
  'vp eng': 'VP Engineering',
  'vp engineering': 'VP Engineering',
  'vp of engineering': 'VP Engineering',
  'vice president of engineering': 'VP Engineering',
  'vp sales': 'VP Sales',
  'vp of sales': 'VP Sales',
  'vice president of sales': 'VP Sales',
  'vp marketing': 'VP Marketing',
  'vp of marketing': 'VP Marketing',
  'head of ops': 'Head of Operations',
  'head of operations': 'Head of Operations',
  'eng manager': 'Engineering Manager',
  'engineering manager': 'Engineering Manager',
  swe: 'Software Engineer',
  'software engineer': 'Software Engineer',
  'account executive': 'Account Executive',
  ae: 'Account Executive',
};

/** Canonical form of a raw title, or null when nothing maps/changes. */
function canonicalTitle(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.$/, '');
  return TITLE_CANON[key] ?? null;
}

/** Seniority tag derived from a (canonical or raw) title, or null. */
function seniorityFromTitle(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const t = (canonicalTitle(raw) ?? raw).trim().toLowerCase();
  if (/^c[a-z]o$/.test(t) || t.includes('chief') || t.includes('founder')) {
    return 'c-level';
  }
  if (t.startsWith('vp') || t.includes('vice president')) return 'vp';
  if (t.includes('director')) return 'director';
  if (t.includes('manager') || t.startsWith('head of')) return 'manager';
  return 'ic';
}

// --- deterministicEnrich -----------------------------------------------------------

/**
 * Source-of-truth enrichment: derive normalizations ONLY from data already
 * on the record. No employee counts, no funding rounds, no industry guesses
 * — nothing the record itself cannot prove. Every emitted field is marked
 * source='deterministic' and skips no-op changes (after === before).
 */
export function deterministicEnrich(record: Record<string, any>): EnrichField[] {
  const fields: EnrichField[] = [];
  const emit = (field: string, before: any, after: any, confidence: number) => {
    if (after == null || after === before) return;
    fields.push({ field, before: before ?? null, after, source: 'deterministic', confidence });
  };

  // 1) Domain — normalized from email or website already on the record.
  const domain = deriveDomain(record);
  if (domain) emit('domain', record.domain ?? null, domain, 0.95);

  // 2) Name — titlecase what is already there.
  if (typeof record.name === 'string' && record.name.trim()) {
    emit('name', record.name, titleCaseName(record.name), 0.9);
  }

  // 3) Title — canonical map lookup on the existing title.
  const canon = canonicalTitle(record.title);
  if (canon) emit('title', record.title ?? null, canon, 0.9);

  // 4) Seniority tag — derived from the existing title.
  const seniority = seniorityFromTitle(record.title);
  if (seniority) emit('seniority', record.seniority ?? null, seniority, 0.85);

  return fields;
}

// --- Derivability guard --------------------------------------------------------------

/**
 * True when `field.after` can be re-derived from the record itself — the
 * anti-hallucination fence for the deterministic path. Firmographics and
 * any unknown field are NEVER derivable: they cannot come from the record,
 * so on the deterministic path they cannot exist.
 */
export function hasDerivableSource(
  record: Record<string, any>,
  field: EnrichField,
): boolean {
  switch (field.field) {
    case 'domain':
      return deriveDomain(record) === field.after;
    case 'name':
      return (
        typeof record.name === 'string' &&
        record.name.trim() !== '' &&
        titleCaseName(record.name) === field.after
      );
    case 'title':
      return canonicalTitle(record.title) === field.after;
    case 'seniority':
      return seniorityFromTitle(record.title) === field.after;
    default:
      return false; // firmographics etc. have no in-record source — drop.
  }
}

// --- buildProposal ---------------------------------------------------------------------

/** Best-effort record id/type so a proposal is always addressable. */
function recordIdentity(record: Record<string, any>): {
  recordId: string;
  recordType: 'account' | 'person';
} {
  const recordId = typeof record.id === 'string' ? record.id : String(record.id ?? '');
  const recordType: 'account' | 'person' =
    record.recordType === 'account' || record.recordType === 'person'
      ? record.recordType
      : typeof record.email === 'string' || typeof record.title === 'string'
        ? 'person'
        : 'account';
  return { recordId, recordType };
}

/**
 * Build the reviewable proposal. Worker fields (fed in by the caller from
 * argoWorker) win when present; when the worker is unavailable or returned
 * nothing, fall back to {@link deterministicEnrich} and mark the proposal
 * generatedBy='deterministic'. On the deterministic path every field must
 * pass {@link hasDerivableSource} — fabricated values never reach the diff.
 */
export function buildProposal(
  record: Record<string, any>,
  workerFields: EnrichField[] | null,
): EnrichProposal {
  const usable = (workerFields ?? []).filter(
    (f): f is EnrichField =>
      typeof f === 'object' &&
      f !== null &&
      typeof f.field === 'string' &&
      f.field.trim() !== '' &&
      f.after != null,
  );
  const generatedBy: EnrichProposal['generatedBy'] =
    usable.length > 0 ? 'worker' : 'deterministic';
  const fields =
    generatedBy === 'worker'
      ? usable
      : deterministicEnrich(record).filter((f) => hasDerivableSource(record, f));

  const { recordId, recordType } = recordIdentity(record);
  return { recordId, recordType, fields, risk: classifyRisk(fields), generatedBy };
}

// --- classifyRisk ----------------------------------------------------------------------

/**
 * A proposal is 'high' risk when ANY field belongs to a gated-by-default
 * class (contact-role, firmographic) — one risky field taints the batch
 * for headline purposes; per-field tiering still happens in
 * {@link autoApplyDecision}.
 */
export function classifyRisk(fields: EnrichField[]): 'low' | 'high' {
  return fields.some((f) => HIGH_RISK_CLASSES.has(classOfField(f.field)))
    ? 'high'
    : 'low';
}

// --- autoApplyDecision -------------------------------------------------------------------

/**
 * Tiered autonomy for the proposal: a field auto-applies ONLY when its
 * class's earned tier is 'auto' AND its confidence clears the policy floor.
 * Everything else — gated classes, shaky confidence — routes to the human
 * approvals queue as a before/after diff. Deterministic and order-stable.
 */
export function autoApplyDecision(
  proposal: EnrichProposal,
  policy: { tier: Record<EnrichClass, 'auto' | 'gate'>; minConfidence: number },
): { autoApply: EnrichField[]; gate: EnrichField[] } {
  const autoApply: EnrichField[] = [];
  const gate: EnrichField[] = [];
  for (const field of proposal.fields) {
    const tier = policy.tier[classOfField(field.field)] ?? 'gate';
    if (tier === 'auto' && field.confidence >= policy.minConfidence) {
      autoApply.push(field);
    } else {
      gate.push(field);
    }
  }
  return { autoApply, gate };
}

// --- toActionDiff ----------------------------------------------------------------------

/**
 * Package the proposal as a reversible action diff for the approvals inbox
 * and the sealed ledger. `changes` carries the full before-state per field,
 * so a compensating restore (reversal.ts style) is always compilable — that
 * is what makes `reversible: true` a fact rather than a flag.
 */
export function toActionDiff(proposal: EnrichProposal): EnrichActionDiff {
  const n = proposal.fields.length;
  const fieldList = proposal.fields.map((f) => f.field).join(', ');
  const provenanceNote =
    `Enrichment (${proposal.generatedBy}) proposes ${n} field change${n === 1 ? '' : 's'}` +
    ` on ${proposal.recordType} ${proposal.recordId}` +
    (n > 0 ? ` [${fieldList}]` : '') +
    `; risk ${proposal.risk}; before-values sealed for revert.`;
  return {
    recordId: proposal.recordId,
    changes: proposal.fields,
    reversible: true,
    provenanceNote,
  };
}

// --- summarizeEnrichRun ------------------------------------------------------------------

/** Plain-language summary of one enrichment run — what the panel headlines. */
export interface EnrichRunSummary {
  /** Total proposed field changes. */
  total: number;
  /** How many auto-applied under the earned tier (reversible). */
  autoApplied: number;
  /** How many are gated, waiting on the operator's approval. */
  gated: number;
  /** One-line, human-readable result ("3 field updates proposed — …"). */
  headline: string;
  /**
   * Honest provenance line: who produced these fields and, when the AI
   * worker is NOT connected, what connecting it would add.
   */
  sourceNote: string;
}

/**
 * Pure, human-honest summary of an enrichment run for the UI. Never
 * overstates: the deterministic path is described as "derived from data
 * already on the record", and an unconfigured worker is called out
 * explicitly so an empty result reads as "connect AI", not "broken".
 */
export function summarizeEnrichRun(
  proposal: EnrichProposal,
  decision: { autoApply: EnrichField[]; gate: EnrichField[] },
  workerConfigured: boolean,
): EnrichRunSummary {
  const total = proposal.fields.length;
  const autoApplied = decision.autoApply.length;
  const gated = decision.gate.length;

  let headline: string;
  if (total === 0) {
    headline = 'No changes proposed — every derivable field is already normalized.';
  } else {
    const parts: string[] = [];
    if (autoApplied > 0) {
      parts.push(
        `${autoApplied} auto-applied under the earned tier (reversible from the ledger)`,
      );
    }
    if (gated > 0) {
      parts.push(`${gated} awaiting your approval`);
    }
    headline =
      `${total} field update${total === 1 ? '' : 's'} proposed` +
      (parts.length > 0 ? ` — ${parts.join(', ')}` : '') +
      '.';
  }

  let sourceNote: string;
  if (proposal.generatedBy === 'worker') {
    sourceNote =
      'Researched by the AI agent — review each before → after diff before approving.';
  } else if (workerConfigured) {
    sourceNote =
      'The AI agent returned nothing usable, so only fields derivable from data ' +
      'already on the record are shown — nothing was invented.';
  } else {
    sourceNote =
      'AI worker not connected — this pass only normalizes data already on the ' +
      'record. Connect the agent worker to research the company itself.';
  }

  return { total, autoApplied, gated, headline, sourceNote };
}
